import React from 'react';
import {FieldArray, FieldArrayRenderProps} from "formik";
import Field from "components/Form/Field";
import {GlossaryEntry, LessonActivityPlanData, Resource} from "types/LessonPlan";
import Button from "components/Button";
import {PlusIcon, TrashIcon} from "@heroicons/react/solid";
import {Exhibit} from "types/Exhibit";
import Selector from "components/Form/Selector";
import {toInt} from "services/number";
import Uploader from "components/Form/Uploader";
import {MediaFile} from "types/MediaFile";
import FieldHeader from "components/Form/FieldHeader";
import {useOperator} from "context/OperatorContext";

type Props = {
    media: Array<MediaFile>
    exhibits: Array<Exhibit>
    activityCount: number
    activity: LessonActivityPlanData
    index: number
    setFieldValue: (field: string, value: any) => any,
    removeActivity: () => any
}

const emptyGlossaryEntry: GlossaryEntry = {
    word: '',
    definition: ''
}

const emptyResource: Resource = {
    name: '',
    value: ''
}

function LessonActivityPlanForm(props: Props) {
    const {media, exhibits, activityCount, activity, index, setFieldValue, removeActivity} = props

    const {termFor} = useOperator()

    return (
        <div className="bg-gray-50 shadow-sm rounded-lg my-4">
            <div className="px-4 py-3 sm:px-6 flex justify-between items-center">
                <span className="font-medium">Activity {index + 1}</span>

                {activityCount > 1?
                    <Button color={Button.colors.WHITE}
                            size={Button.sizes.X_SMALL}
                            icon={TrashIcon}
                            onClick={removeActivity}
                    />
                : null}
            </div>

            <div className="px-4 pb-5 sm:px-6 sm:pb-6 text-sm">
                <div className="mt-6 grid grid-cols-1 gap-y-6 gap-x-4 sm:grid-cols-6">

                    {/* Duration */}
                    <div className="sm:col-span-2 xl:col-span-1">
                        <Field type="number"
                               label="Duration"
                               addon="minutes"
                               name={`activities.${index}.duration`}
                               id={`activities.${index}.duration`}
                               autoComplete="off"
                               min={1}
                               step={1}
                               required
                        />
                    </div>

                    {/* Name */}
                    <div className="sm:col-span-4 xl:col-span-5">
                        <Field type="text"
                               label="Name"
                               name={`activities.${index}.name`}
                               id={`activities.${index}.name`}
                               autoComplete="name"
                               maxLength="255"
                               required
                        />
                    </div>

                    {/* Description */}
                    <div className="sm:col-span-6">
                        <Field as="textarea"
                               rows={4}
                               label="Description"
                               name={`activities.${index}.description`}
                               id={`activities.${index}.description`}
                               autoComplete="off"
                               maxLength="50000"
                               required
                        />
                    </div>

                    {/* Media */}
                    <div className="sm:col-span-6">
                        <FieldHeader fieldId={`activities.${index}.media`} label="Media" hint="optional" />
                        <div className="mt-1">
                            <Uploader
                                id={`activities-${index}-media`}
                                files={media}
                                onChange={files => setFieldValue(`activities.${index}.media`, files.map(file => ({id: file.id})))}
                            />
                        </div>
                    </div>

                    {/* Glossary */}
                    <div className="sm:col-span-6">
                        <fieldset>
                            <legend className="w-full">
                                <FieldHeader label="Glossary" hint="optional" condensed />
                            </legend>

                            <FieldArray name={`activities.${index}.glossary`}>
                                {({push, remove}: FieldArrayRenderProps) => (
                                    <div>
                                        <ul>
                                            {activity.glossary.map((entry, entryIndex) => (
                                                <li key={entryIndex} className="my-2">
                                                    <div className="flex items-center justify-between space-x-1 lg:space-x-4">
                                                        <div className="w-full flex space-x-1">
                                                            <div className="w-1/3">
                                                                <label
                                                                    htmlFor={`activities.${index}.glossary.${entryIndex}.word`}
                                                                    className="sr-only"
                                                                >Word</label>
                                                                <Field
                                                                    type="text"
                                                                    name={`activities.${index}.glossary.${entryIndex}.word`}
                                                                    id={`activities.${index}.glossary.${entryIndex}.word`}
                                                                    autoComplete="off"
                                                                    maxLength="255"
                                                                    placeholder="Word"
                                                                />
                                                            </div>
                                                            <div className="w-full">
                                                                <label
                                                                    htmlFor={`activities.${index}.glossary.${entryIndex}.definition`}
                                                                    className="sr-only"
                                                                >Definition</label>
                                                                <Field
                                                                    type="text"
                                                                    name={`activities.${index}.glossary.${entryIndex}.definition`}
                                                                    id={`activities.${index}.glossary.${entryIndex}.definition`}
                                                                    autoComplete="off"
                                                                    maxLength="255"
                                                                    placeholder="Definition"
                                                                />
                                                            </div>
                                                        </div>
                                                        <div className="flex-grow-0">
                                                            <Button
                                                                size={Button.sizes.TINY}
                                                                color={Button.colors.WHITE}
                                                                icon={TrashIcon}
                                                                onClick={() => remove(entryIndex)}
                                                            />
                                                        </div>
                                                    </div>
                                                </li>
                                            ))}
                                        </ul>

                                        <div className="mt-1">
                                            <Button
                                                color={Button.colors.SECONDARY}
                                                icon={PlusIcon}
                                                size={Button.sizes.TINY}
                                                onClick={() => push({...emptyGlossaryEntry})}
                                            >
                                                Add Entry
                                            </Button>
                                        </div>
                                    </div>
                                )}
                            </FieldArray>
                        </fieldset>
                    </div>

                    {/* Resources */}
                    <div className="sm:col-span-6">
                        <fieldset>
                            <legend className="w-full">
                                <FieldHeader label="Resources" hint="optional" condensed />
                            </legend>

                            <FieldArray name={`activities.${index}.resources`}>
                                {({push, remove}: FieldArrayRenderProps) => (
                                    <div>
                                        <ul>
                                            {activity.resources.map((resource, resourceIndex) => (
                                                <li key={resourceIndex} className="my-2">
                                                    <div className="flex items-center justify-between space-x-1 lg:space-x-4">
                                                        <div className="w-full flex space-x-1">
                                                            <div className="w-1/3">
                                                                <label
                                                                    htmlFor={`activities.${index}.resources.${resourceIndex}.name`}
                                                                    className="sr-only"
                                                                >Name</label>
                                                                <Field
                                                                    type="text"
                                                                    name={`activities.${index}.resources.${resourceIndex}.name`}
                                                                    id={`activities.${index}.resources.${resourceIndex}.name`}
                                                                    autoComplete="off"
                                                                    maxLength="255"
                                                                    placeholder="Name"
                                                                />
                                                            </div>
                                                            <div className="w-full">
                                                                <label
                                                                    htmlFor={`activities.${index}.resources.${resourceIndex}.value`}
                                                                    className="sr-only"
                                                                >Value</label>
                                                                <Field
                                                                    type="text"
                                                                    name={`activities.${index}.resources.${resourceIndex}.value`}
                                                                    id={`activities.${index}.resources.${resourceIndex}.value`}
                                                                    autoComplete="off"
                                                                    maxLength="255"
                                                                    placeholder="Value"
                                                                />
                                                            </div>
                                                        </div>
                                                        <div className="flex-grow-0">
                                                            <Button
                                                                size={Button.sizes.TINY}
                                                                color={Button.colors.WHITE}
                                                                icon={TrashIcon}
                                                                onClick={() => remove(resourceIndex)}
                                                            />
                                                        </div>
                                                    </div>
                                                </li>
                                            ))}
                                        </ul>

                                        <div className="mt-1">
                                            <Button
                                                color={Button.colors.SECONDARY}
                                                icon={PlusIcon}
                                                size={Button.sizes.TINY}
                                                onClick={() => push({...emptyResource})}
                                            >
                                                Add Resource
                                            </Button>
                                        </div>
                                    </div>
                                )}
                            </FieldArray>
                        </fieldset>
                    </div>

                    {/* Exhibits */}
                    <div className="sm:col-span-6 lg:col-span-3">
                        <fieldset>
                            <legend className="w-full">
                                <FieldHeader label={termFor('Exhibits')} hint="optional" condensed />
                            </legend>

                            <FieldArray name={`activities.${index}.exhibits`}>
                                {({push, remove}: FieldArrayRenderProps) => (
                                    <div>
                                        <ul>
                                            {activity.exhibits.map((exhibitId, exhibitIndex) => (
                                                <li key={exhibitIndex} className="my-2">
                                                    <div className="flex items-center justify-between space-x-1 lg:space-x-4">
                                                        <div className="w-full">
                                                            <Selector
                                                                items={exhibits.map(exhibit => ({id: exhibit.id, name: exhibit.name, description: exhibit.type.name}))}
                                                                selectedId={exhibitId}
                                                                onChange={item => setFieldValue(`activities.${index}.exhibits.${exhibitIndex}`, toInt(item.id))}
                                                            />
                                                        </div>
                                                        <div className="flex-grow-0">
                                                            <Button
                                                                size={Button.sizes.TINY}
                                                                color={Button.colors.WHITE}
                                                                icon={TrashIcon}
                                                                onClick={() => remove(exhibitIndex)}
                                                            />
                                                        </div>
                                                    </div>
                                                </li>
                                            ))}
                                        </ul>

                                        <div className="mt-1">
                                            <Button
                                                color={Button.colors.SECONDARY}
                                                icon={PlusIcon}
                                                size={Button.sizes.TINY}
                                                onClick={() => push(0)}
                                            >
                                                Add {termFor('Exhibit')}
                                            </Button>
                                        </div>
                                    </div>
                                )}
                            </FieldArray>
                        </fieldset>
                    </div>
                </div>
            </div>
        </div>
    );
}

export default LessonActivityPlanForm;